import React, { MouseEvent, useCallback } from 'react';
import { Link, To } from 'react-router-dom-v5-compat';
import { useDispatch, useSelector } from 'react-redux';
import type { AppState } from '~/reducers';
import { dispatchNativeCommandGuarded } from '~/domain/native/store/dispatch-native-command-guarded';
import { NativeCommandEnum } from '~/domain/native/models/commands';
import type { AppDispatch } from '~/store/Store';

export interface WebviewEnabledLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: To;
  state?: any;
  id?: string;
  className?: string;
  title?: string;
  replace?: boolean;
  onClick?(event: MouseEvent<HTMLAnchorElement>): void;
  children?: React.ReactNode;
  forwardToNative?: boolean;
}

const WebviewEnabledLink: React.FC<WebviewEnabledLinkProps> = ({
  to,
  state,
  id,
  className,
  title,
  replace,
  onClick,
  children,
  forwardToNative = true,
  ...rest
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { onWebViewInitialized } = useSelector((s: AppState) => s.native);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        onClick(e);
      }
      if (e.defaultPrevented) {
        return;
      }

      if (onWebViewInitialized && forwardToNative) {
        e.preventDefault();

        if (typeof to === 'string') {
          const [pathname, query = ''] = to.split('?');
          // search already includes the ? prefix — pass directly to avoid double ?
          const search = query ? `?${query}` : '';
          dispatch(
            dispatchNativeCommandGuarded({
              name: NativeCommandEnum.NavigateTo,
              params: {
                path: pathname,
                search,
                data: state,
              },
            })
          );
        } else {
          const pathname = to.pathname ?? '';
          const search = to.search ?? '';
          dispatch(
            dispatchNativeCommandGuarded({
              name: NativeCommandEnum.NavigateTo,
              params: {
                path: pathname,
                search: search ? `?${search}` : '',
                data: state,
              },
            })
          );
        }
      }
    },
    [dispatch, forwardToNative, onClick, onWebViewInitialized, state, to]
  );

  return (
    <Link
      to={to}
      state={state}
      replace={replace}
      onClick={handleClick}
      className={className}
      title={title}
      id={id}
      {...rest}
    >
      {children}
    </Link>
  );
};

export default WebviewEnabledLink;
